"use server";

import { prisma } from "@/lib/prisma";
import { auth } from "@/lib/auth";
import { revalidatePath } from "next/cache";
import type { Role } from "@prisma/client";
import { ensureKudosAccount } from "./kudos";
import { dbg } from "@/lib/debug-log";

// ─── Internal helpers ─────────────────────────────────────────────────────────

async function requireAdmin() {
  const session = await auth();
  if (!session || session.user.role !== "ADMIN") throw new Error("Unauthorized");
  return session;
}

async function requireAdminOrParent() {
  const session = await auth();
  if (!session || (session.user.role !== "ADMIN" && session.user.role !== "PARENT"))
    throw new Error("Unauthorized");
  return session;
}

async function sendEmail(to: string, subject: string, text: string, html: string) {
  dbg.info("admin/email", `sending "${subject}" to ${to}`);
  const nodemailer = await import("nodemailer");
  const transport = nodemailer.createTransport({
    host: process.env.EMAIL_SERVER_HOST,
    port: Number(process.env.EMAIL_SERVER_PORT),
    auth: {
      user: process.env.EMAIL_SERVER_USER,
      pass: process.env.EMAIL_SERVER_PASSWORD,
    },
  });
  try {
    await transport.sendMail({ to, from: process.env.EMAIL_FROM, subject, text, html });
    dbg.info("admin/email", `sent OK — "${subject}" to ${to}`);
  } catch (err) {
    dbg.error("admin/email", `send FAILED — "${subject}" to ${to}`, { error: String(err) });
    throw err;
  }
}

// Fetch site identity strings from SiteConfig for use in email templates.
// Returns sensible generic fallbacks so email still sends on a fresh install.
async function getSiteIdentity(): Promise<{ siteName: string; subtitle: string }> {
  const [siteNameRow, placeholderRow, dueDateRow] = await Promise.all([
    prisma.siteConfig.findUnique({ where: { key: "site_name" } }),
    prisma.siteConfig.findUnique({ where: { key: "baby_name_placeholder" } }),
    prisma.siteConfig.findUnique({ where: { key: "due_date" } }),
  ]);

  const siteName    = siteNameRow?.value    ?? "Baby Name Championship";
  const placeholder = placeholderRow?.value ?? "Baby";
  const dueDateRaw  = dueDateRow?.value;

  let subtitle = siteName;
  if (dueDateRaw) {
    try {
      const d = new Date(dueDateRaw + "T12:00:00");
      subtitle = `${siteName} · ${placeholder} · Due ${d.toLocaleDateString("en-US", { month: "numeric", day: "numeric", year: "numeric" })}`;
    } catch {
      subtitle = `${siteName} · ${placeholder}`;
    }
  } else {
    subtitle = `${siteName} · ${placeholder}`;
  }

  return { siteName, subtitle };
}

function inviteEmailHtml(loginUrl: string, displayName: string, siteName: string, subtitle: string) {
  return `
    <div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:linear-gradient(135deg,#f3e8ff,#fce7f3);border-radius:16px;">
      <h1 style="color:#6b21a8;font-size:28px;margin-bottom:8px;text-align:center;">🏆</h1>
      <h2 style="color:#6b21a8;text-align:center;margin-bottom:4px;">You're on the team!</h2>
      <p style="color:#7e22ce;text-align:center;margin-bottom:32px;font-size:14px;">${subtitle}</p>
      <p style="color:#581c87;margin-bottom:8px;">Hey${displayName ? ` ${displayName}` : ""}! You've been invited to help choose a baby name in ${siteName}.</p>
      <p style="color:#581c87;margin-bottom:24px;">Submit names, watch the bracket unfold, and vote when tie-breakers happen — and earn kudos for good picks!</p>
      <a href="${loginUrl}" style="display:block;text-align:center;background:#7c3aed;color:white;padding:16px 32px;border-radius:12px;text-decoration:none;font-weight:bold;font-size:16px;margin-bottom:24px;">Get in the Game →</a>
      <p style="color:#9333ea;font-size:12px;text-align:center;">Sign in with this email address to access the tournament.</p>
    </div>`;
}

function approvalEmailHtml(loginUrl: string, name: string, siteName: string, subtitle: string) {
  return `
    <div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:linear-gradient(135deg,#f3e8ff,#fce7f3);border-radius:16px;">
      <h1 style="color:#6b21a8;font-size:28px;margin-bottom:8px;text-align:center;">🏆</h1>
      <h2 style="color:#6b21a8;text-align:center;margin-bottom:4px;">You're in, ${name}!</h2>
      <p style="color:#7e22ce;text-align:center;margin-bottom:32px;font-size:14px;">${subtitle}</p>
      <p style="color:#581c87;margin-bottom:24px;">Your access request has been approved — welcome to ${siteName}! You start with 100 kudos to bet on your favourite names.</p>
      <a href="${loginUrl}" style="display:block;text-align:center;background:#7c3aed;color:white;padding:16px 32px;border-radius:12px;text-decoration:none;font-weight:bold;font-size:16px;margin-bottom:24px;">Join the Tournament →</a>
    </div>`;
}

// ─── User management ──────────────────────────────────────────────────────────

export async function inviteUser(formData: FormData) {
  const session = await requireAdminOrParent();
  const email = formData.get("email")?.toString().trim().toLowerCase() ?? "";
  const displayName = formData.get("displayName")?.toString().trim() ?? "";
  const role = (formData.get("role")?.toString() ?? "FAMILY") as Role;
  if (!email) return;

  dbg.info("admin", `inviteUser: ${email} as ${role}`, {
    invitedBy: session.user.email,
    displayName,
  });

  // Pre-create so their role is correct when they first sign in via magic link
  const user = await prisma.user.upsert({
    where: { email },
    create: { email, displayName, role },
    update: { role }, // don't overwrite displayName if they've already customised it
  });

  // Seed kudos account (safe to call multiple times — uses upsert)
  ensureKudosAccount(user.id).catch((err) =>
    dbg.error("admin", `ensureKudosAccount failed for invited user ${email}`, { error: String(err) })
  );

  const loginUrl = `${process.env.NEXTAUTH_URL}/login`;
  const adminUrl = `${process.env.NEXTAUTH_URL}/admin/users`;
  const { siteName, subtitle } = await getSiteIdentity();

  // Send invite to the new member
  await sendEmail(
    email,
    `🏆 You're invited to ${siteName}!`,
    `You've been invited to help choose a baby name in ${siteName}! Sign in at: ${loginUrl}`,
    inviteEmailHtml(loginUrl, displayName, siteName, subtitle),
  );

  // Notify all admins
  const admins = await prisma.user.findMany({ where: { role: "ADMIN" }, select: { email: true } });
  for (const admin of admins) {
    sendEmail(
      admin.email,
      `🏆 Invite sent to ${displayName || email}`,
      `${session.user.displayName || session.user.email} invited ${displayName || email} (${email}) as ${role}.\n\nManage users: ${adminUrl}`,
      `<div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#f3e8ff;border-radius:16px;">
        <h2 style="color:#6b21a8;">🏆 Invite Sent</h2>
        <p><strong>${displayName || email}</strong> (${email}) was invited as <strong>${role}</strong>.</p>
        <p style="color:#7e22ce;font-size:14px;">Invited by: ${session.user.displayName || session.user.email}</p>
        <a href="${adminUrl}" style="display:block;text-align:center;background:#7c3aed;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold;margin-top:24px;">Manage Users →</a>
      </div>`,
    ).catch((err) => dbg.warn("admin", `admin notification email failed for inviteUser`, { adminEmail: admin.email, error: String(err) }));
  }

  dbg.info("admin", `inviteUser complete: ${email} (userId=${user.id})`);
  revalidatePath("/admin/users");
}

export async function updateUserRole(formData: FormData) {
  const session = await requireAdminOrParent();
  const userId = formData.get("userId")?.toString() ?? "";
  const role = formData.get("role")?.toString() as Role;
  if (!userId || !role) return;

  dbg.info("admin", `updateUserRole: userId=${userId} → ${role}`, { changedBy: session.user.email });
  await prisma.user.update({ where: { id: userId }, data: { role } });
  revalidatePath("/admin/users");
}

export async function deleteUser(formData: FormData) {
  const session = await requireAdminOrParent();
  const userId = formData.get("userId")?.toString() ?? "";

  // Guard: can't delete yourself
  if (!userId || userId === session.user.id) {
    dbg.warn("admin", `deleteUser blocked — attempted self-delete`, { userId, requestedBy: session.user.email });
    return;
  }

  const target = await prisma.user.findUnique({ where: { id: userId }, select: { email: true, displayName: true } });
  dbg.info("admin", `deleteUser: ${target?.email ?? userId}`, { deletedBy: session.user.email });

  // Delete related records that don't cascade automatically
  await prisma.vote.deleteMany({ where: { userId } });
  await prisma.lCKVote.deleteMany({ where: { userId } });
  await prisma.user.delete({ where: { id: userId } });

  dbg.info("admin", `deleteUser complete: ${target?.email ?? userId}`);
  revalidatePath("/admin/users");
}

export async function resendInvite(formData: FormData) {
  const session = await requireAdminOrParent();
  const userId = formData.get("userId")?.toString() ?? "";
  if (!userId) return;

  const user = await prisma.user.findUnique({ where: { id: userId } });
  if (!user) {
    dbg.warn("admin", `resendInvite — user not found`, { userId });
    return;
  }

  dbg.info("admin", `resendInvite: ${user.email}`, { resentBy: session.user.email });

  const loginUrl = `${process.env.NEXTAUTH_URL}/login`;
  const adminUrl = `${process.env.NEXTAUTH_URL}/admin/users`;
  const { siteName, subtitle } = await getSiteIdentity();

  // Resend invite to the member
  await sendEmail(
    user.email,
    `🏆 ${siteName} — reminder to join!`,
    `Don't forget to join the tournament! Sign in at: ${loginUrl}`,
    inviteEmailHtml(loginUrl, user.displayName, siteName, subtitle),
  );

  // Notify all admins
  const admins = await prisma.user.findMany({ where: { role: "ADMIN" }, select: { email: true } });
  for (const admin of admins) {
    sendEmail(
      admin.email,
      `🏆 Invite resent to ${user.displayName || user.email}`,
      `${session.user.displayName || session.user.email} resent the invite to ${user.displayName || user.email} (${user.email}).\n\nManage users: ${adminUrl}`,
      `<div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#f3e8ff;border-radius:16px;">
        <h2 style="color:#6b21a8;">🏆 Invite Resent</h2>
        <p>Reminder sent to <strong>${user.displayName || user.email}</strong> (${user.email}).</p>
        <p style="color:#7e22ce;font-size:14px;">Resent by: ${session.user.displayName || session.user.email}</p>
        <a href="${adminUrl}" style="display:block;text-align:center;background:#7c3aed;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold;margin-top:24px;">Manage Users →</a>
      </div>`,
    ).catch((err) => dbg.warn("admin", `admin notification email failed for resendInvite`, { adminEmail: admin.email, error: String(err) }));
  }

  revalidatePath("/admin/users");
}

// ─── Access requests ──────────────────────────────────────────────────────────

export async function approveRequest(formData: FormData) {
  const session = await requireAdminOrParent();
  const requestId = formData.get("requestId")?.toString() ?? "";
  const role = (formData.get("role")?.toString() ?? "FAMILY") as Role;
  if (!requestId) return;

  const request = await prisma.accessRequest.findUnique({ where: { id: requestId } });
  if (!request) {
    dbg.warn("admin", `approveRequest — request not found`, { requestId });
    return;
  }

  dbg.info("admin", `approveRequest: ${request.email} as ${role}`, { approvedBy: session.user.email, requestId });

  const user = await prisma.user.upsert({
    where: { email: request.email },
    create: { email: request.email, displayName: request.name, role },
    update: { role },
  });

  // Seed kudos account for newly approved user
  ensureKudosAccount(user.id).catch((err) =>
    dbg.error("admin", `ensureKudosAccount failed for approved user ${request.email}`, { error: String(err) })
  );

  await prisma.accessRequest.update({ where: { id: requestId }, data: { status: "APPROVED" } });

  const loginUrl = `${process.env.NEXTAUTH_URL}/login`;
  const { siteName, subtitle } = await getSiteIdentity();

  await sendEmail(
    request.email,
    `🏆 Your ${siteName} access has been approved!`,
    `You're in! Sign in at: ${loginUrl}`,
    approvalEmailHtml(loginUrl, request.name, siteName, subtitle),
  );

  dbg.info("admin", `approveRequest complete: ${request.email} (userId=${user.id})`);
  revalidatePath("/admin/users");
}

export async function denyRequest(formData: FormData) {
  const session = await requireAdminOrParent();
  const requestId = formData.get("requestId")?.toString() ?? "";
  if (!requestId) return;

  const request = await prisma.accessRequest.findUnique({ where: { id: requestId }, select: { email: true } });
  dbg.info("admin", `denyRequest: ${request?.email ?? requestId}`, { deniedBy: session.user.email });

  await prisma.accessRequest.update({ where: { id: requestId }, data: { status: "DENIED" } });
  revalidatePath("/admin/users");
}

// ─── Public: submit access request (no auth required) ────────────────────────

export async function submitAccessRequest(formData: FormData): Promise<{ error?: string; alreadyUser?: boolean; success?: boolean }> {
  const name = formData.get("name")?.toString().trim() ?? "";
  const email = formData.get("email")?.toString().trim().toLowerCase() ?? "";
  const message = formData.get("message")?.toString().trim() ?? "";
  if (!name || !email) return { error: "Name and email are required." };

  dbg.info("admin", `submitAccessRequest: ${name} <${email}>`, { hasMessage: !!message });

  // If they're already a registered user, just tell them to sign in normally
  const existing = await prisma.user.findUnique({ where: { email } });
  if (existing) {
    dbg.info("admin", `submitAccessRequest — ${email} is already a user`);
    return { alreadyUser: true };
  }

  await prisma.accessRequest.upsert({
    where: { email },
    create: { name, email, message: message || null, status: "PENDING" },
    update: { name, message: message || null, status: "PENDING" },
  });

  // Notify all admins by email
  const admins = await prisma.user.findMany({ where: { role: "ADMIN" }, select: { email: true } });
  const adminUrl = `${process.env.NEXTAUTH_URL}/admin/users`;
  const { siteName } = await getSiteIdentity();

  for (const admin of admins) {
    sendEmail(
      admin.email,
      `🏆 New ${siteName} access request from ${name}`,
      `${name} (${email}) wants in.\nMessage: ${message || "(none)"}\nReview at: ${adminUrl}`,
      `<div style="font-family:sans-serif;max-width:480px;margin:0 auto;padding:32px;background:#f3e8ff;border-radius:16px;">
        <h2 style="color:#6b21a8;">🏆 New Access Request</h2>
        <p><strong>Name:</strong> ${name}</p>
        <p><strong>Email:</strong> ${email}</p>
        ${message ? `<p><strong>Message:</strong> &ldquo;${message}&rdquo;</p>` : ""}
        <a href="${adminUrl}" style="display:block;text-align:center;background:#7c3aed;color:white;padding:12px 24px;border-radius:8px;text-decoration:none;font-weight:bold;margin-top:24px;">Review Request →</a>
      </div>`,
    ).catch((err) => dbg.warn("admin", `admin notification email failed for submitAccessRequest`, { adminEmail: admin.email, error: String(err) }));
  }

  dbg.info("admin", `submitAccessRequest queued for review: ${email}`);
  return { success: true };
}
